import type { RewardStatus } from '@/lib/api';

interface RewardStatusCardProps {
  status: RewardStatus;
  isLoading?: boolean;
  onBadgeClick?: () => void;
}

export default function RewardStatusCard({ status, isLoading, onBadgeClick }: RewardStatusCardProps) {
  if (isLoading) {
    return (
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 mb-6">
        <div className="animate-pulse flex items-start gap-6">
          <div className="w-24 h-16 bg-amber-200 rounded-lg" />
          <div className="flex-1 space-y-3">
            <div className="h-6 bg-amber-200 rounded w-1/3" />
            <div className="h-4 bg-amber-200 rounded w-1/4" />
            <div className="h-2 bg-amber-200 rounded w-full mt-4" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 mb-6">
      <h2 className="text-sm font-medium text-amber-800 uppercase tracking-wide mb-4">
        Your Status
      </h2>

      <div className="flex flex-col sm:flex-row items-start gap-6">
        {/* Badge Image - Credit card style */}
        <div className="flex-shrink-0">
          <button
            onClick={onBadgeClick}
            className="relative cursor-pointer group focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 rounded-lg"
            title="View badge collection"
          >
            <img
              src={status.currentTier.badgeUrl}
              alt={status.currentTier.name}
              className="w-36 h-24 rounded-lg shadow-md object-cover border-2 border-amber-300 transition-transform group-hover:scale-105"
              onError={(e) => {
                // Fallback if image doesn't exist yet
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                if (target.parentElement) {
                  target.parentElement.innerHTML = `
                    <div class="w-36 h-24 rounded-lg shadow-md border-2 border-amber-300 bg-gradient-to-br from-amber-100 to-orange-200 flex items-center justify-center group-hover:scale-105 transition-transform">
                      <span class="text-3xl">🏙️</span>
                    </div>
                  `;
                }
              }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all flex items-center justify-center">
              <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 bg-black bg-opacity-50 px-2 py-1 rounded">
                View Collection
              </span>
            </div>
          </button>
        </div>

        {/* Status Info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-2xl font-bold text-gray-900">
            {status.currentTier.name}
          </h3>
          <p className="text-gray-600 mt-1">
            {status.totalNights} night{status.totalNights !== 1 ? 's' : ''} stayed
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {status.currentTier.description}
          </p>

          {/* Progress to next tier */}
          {status.nextTier && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-500 mb-1">
                <span>Progress to {status.nextTier.name}</span>
                <span>{status.nextTier.progressPercentage}%</span>
              </div>
              <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-500"
                  style={{ width: `${status.nextTier.progressPercentage}%` }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-1.5">
                {status.nextTier.nightsRemaining} more night{status.nextTier.nightsRemaining !== 1 ? 's' : ''} to go
              </p>
            </div>
          )}

          {/* Max tier reached */}
          {!status.nextTier && status.currentTier.level > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-3 py-1.5 rounded-full text-sm font-medium">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              You've reached the highest tier!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
